"use client";

import { useState } from "react";
import { useSendFeedback } from "../hooks/use-send-feedback";
import { FeedbackType } from "../types/feedback";
import { useActionsTrack } from "@/_old/features/analytics";
import { cn } from "@/core/utils";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { useTranslation } from "react-i18next";

interface FeedbackButtonsProps {
  runId?: string;
  currentFeedback: FeedbackType;
  onFeedbackChange?: (feedback: FeedbackType) => void;
  messageId: string;
  messageContent: string;
}

export function FeedbackButtons({
  runId,
  currentFeedback,
  onFeedbackChange,
  messageId,
  messageContent,
}: FeedbackButtonsProps) {
  const { mutate: sendFeedback, isPending } = useSendFeedback();
  const { onActionClick } = useActionsTrack();
  const { t } = useTranslation();
  const [localFeedback, setLocalFeedback] =
    useState<FeedbackType>(currentFeedback);
  const [copied, setCopied] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState<FeedbackType | null>(
    currentFeedback || null,
  );

  const handleCopy = async () => {
    try {
      // Remove markdown formatting to copy clean text
      let textToCopy = messageContent
        // Remove markdown links [text](url) -> text
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
        // Remove inline code `code`
        .replace(/`([^`]+)`/g, "$1")
        // Remove code blocks ```code```
        .replace(/```[\s\S]*?```/g, "")
        // Remove headers # ## ###
        .replace(/^#{1,6}\s+/gm, "")
        // Remove bold/italic *text* or **text**
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1")
        .replace(/__([^_]+)__/g, "$1")
        .replace(/_([^_]+)_/g, "$1")
        // Remove lists - or *
        .replace(/^[\s]*[-*+]\s+/gm, "")
        // Remove numbered lists 1. 2.
        .replace(/^\d+\.\s+/gm, "")
        // Remove multiple line breaks
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      // Check if clipboard API is available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for non-HTTPS or unsupported browsers
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);

      // Tracking
      onActionClick("CHATBOT_COPY_MESSAGE_CLICK", "CHATBOT", {
        message_id: messageId,
        run_id: runId,
      });

      // Success toast
      toast.success("Mensagem copiada!", {
        duration: 2000,
      });

      // Reset state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Erro ao copiar mensagem", {
        duration: 2000,
      });
    }
  };

  // If there's no content, don't show buttons (message is still loading)
  if (!messageContent) {
    return null;
  }

  const handleFeedback = (isPositive: boolean) => {
    // If there's no runId, don't allow sending feedback (but shows disabled buttons)
    if (!runId) {
      console.warn("⚠️ [FeedbackButtons] Tentativa de feedback sem runId");
      return;
    }
    const newFeedback: FeedbackType = isPositive ? "like" : "dislike";

    // If already selected, remove feedback (toggle)
    if (localFeedback === newFeedback) {
      setLocalFeedback(null);
      onFeedbackChange?.(null);
      setFeedbackSent(null);
      // Remove from localStorage
      try {
        const key = `feedback:${messageId}`;
        localStorage.removeItem(key);
      } catch (error) {
        console.error("Error removing feedback from localStorage:", error);
      }
      return;
    }

    // Update local state immediately (optimistic update)
    setLocalFeedback(newFeedback);
    onFeedbackChange?.(newFeedback);

    // Save to localStorage for persistence
    try {
      const key = `feedback:${messageId}`;
      localStorage.setItem(key, newFeedback);
    } catch (error) {
      console.error("Error saving feedback to localStorage:", error);
    }

    // Send to backend
    sendFeedback(
      {
        run_id: runId,
        is_positive: isPositive,
      },
      {
        onSuccess: () => {
          // Mark feedback as successfully sent
          setFeedbackSent(newFeedback);
          // Tracking
          onActionClick(
            `CHATBOT_FEEDBACK_${isPositive ? "LIKE" : "DISLIKE"}_CLICK`,
            "CHATBOT",
            {
              run_id: runId,
              message_id: messageId,
            },
          );
        },
        onError: (error) => {
          console.error("Error sending feedback:", error);
          // Revert state on error
          setLocalFeedback(currentFeedback);
          onFeedbackChange?.(currentFeedback);
          setFeedbackSent(null);
        },
      },
    );
  };

  const feedback = localFeedback || currentFeedback;
  // Final state: when feedback was successfully sent (no longer pending)
  // If there's already a saved feedback and it's not pending, consider it final
  const isFeedbackFinal =
    (feedbackSent && !isPending && feedback === feedbackSent) ||
    (currentFeedback &&
      !isPending &&
      feedback === currentFeedback &&
      !localFeedback);

  return (
    <TooltipProvider>
      <div className="flex gap-1 items-center mt-2 px-0 md:px-[1.7rem]">
        {/* Copy button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              className={cn(
                "p-1.5 rounded-full transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                // Natural state and feedback: icon only, no background, gray color
                "text-gray-500",
                // Hover: circular lavender-purple light background (only when not copied)
                !copied && "hover:bg-darkgray-700 hover:text-white",
              )}
              aria-label={t("chatbot-button-copy") as string}
            >
              {copied ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h8c1.1 0 2 .9 2 2" />
                </svg>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("chatbot-button-copy")}</p>
          </TooltipContent>
        </Tooltip>

        {/* Like button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => handleFeedback(true)}
              disabled={isPending || !runId}
              className={cn(
                "p-1.5 rounded-full transition-all duration-200",
                "disabled:cursor-not-allowed",
                // Without runId: disabled and opaque
                !runId
                  ? "text-gray-300 cursor-not-allowed"
                  : // Final feedback: filled dark gray icon, no background (highest priority)
                    isFeedbackFinal && feedback === "like"
                    ? "text-gray-700"
                    : // Clicked/Sending (not final): solid blue background with white outline icon
                      feedback === "like" && !isFeedbackFinal
                      ? "bg-darkgray-700 text-white disabled:opacity-100"
                      : // Natural state and hover: gray outline icon, hover with lavender-purple light background
                        "text-gray-500 hover:bg-darkgray-200 hover:text-white disabled:opacity-50",
              )}
              aria-label={t("chatbot-button-like") as string}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill={
                  isFeedbackFinal && feedback === "like"
                    ? "currentColor"
                    : "none"
                }
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M7 10v12" />
                <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
              </svg>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("chatbot-button-like")}</p>
          </TooltipContent>
        </Tooltip>

        {/* Dislike button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => handleFeedback(false)}
              disabled={isPending || !runId}
              className={cn(
                "p-1.5 rounded-full transition-all duration-200",
                "disabled:cursor-not-allowed",
                // Without runId: disabled and opaque
                !runId
                  ? "text-gray-300 cursor-not-allowed"
                  : // Final feedback: filled dark gray icon, no background (highest priority)
                    isFeedbackFinal && feedback === "dislike"
                    ? "text-gray-700"
                    : // Clicked/Sending (not final): solid blue background with white outline icon
                      feedback === "dislike" && !isFeedbackFinal
                      ? "bg-darkgray-700 text-white disabled:opacity-100"
                      : // Natural state and hover: gray outline icon, hover with lavender-purple light background
                        "text-gray-500 hover:bg-darkgray-700 hover:text-white disabled:opacity-50",
              )}
              aria-label={t("chatbot-button-dislike") as string}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill={
                  isFeedbackFinal && feedback === "dislike"
                    ? "currentColor"
                    : "none"
                }
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: "rotate(180deg)" }}
              >
                <path d="M7 10v12" />
                <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
              </svg>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("chatbot-button-dislike")}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
